import React, { useEffect, useState } from "react";
import { Accessibility, X, ALargeSmall, Type, Contrast, Moon, Sun, RefreshCw } from "lucide-react";

const STORAGE_KEY = "a11y_prefs_v1";

const DEFAULT_PREFS = {
  textSize: 1,        // 1 = 100% (default), step 0.1, range 0.85 to 1.4
  highContrast: false,
  darkMode: false,
  reducedMotion: false,
};

function loadPrefs() {
  try {
    return { ...DEFAULT_PREFS, ...JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") };
  } catch {
    return DEFAULT_PREFS;
  }
}

function applyPrefs(prefs) {
  const html = document.documentElement;
  // Text size — scale the root font size; Tailwind classes use rem so they all scale
  html.style.fontSize = `${prefs.textSize * 100}%`;
  // High-contrast class
  html.classList.toggle("a11y-high-contrast", prefs.highContrast);
  // Dark mode (uses .dark class — Tailwind / CSS variables can hook into it)
  html.classList.toggle("dark", prefs.darkMode);
  html.classList.toggle("a11y-dark", prefs.darkMode);
  // Reduced motion
  html.classList.toggle("a11y-reduce-motion", prefs.reducedMotion);
}

/**
 * Floating accessibility toolbar (bottom-right).
 * Persists user choices in localStorage, applies them via classes on <html>.
 * WCAG-friendly: keyboard accessible, ARIA roles, focus management.
 */
export default function AccessibilityToolbar() {
  const [prefs, setPrefs] = useState(loadPrefs);
  const [open, setOpen] = useState(false);

  // Apply on mount + every change
  useEffect(() => {
    applyPrefs(prefs);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); } catch { /* ignore quota */ }
  }, [prefs]);

  const updPrefs = (patch) => setPrefs((p) => ({ ...p, ...patch }));

  const incTextSize = () => updPrefs({ textSize: Math.min(1.4, +(prefs.textSize + 0.1).toFixed(1)) });
  const decTextSize = () => updPrefs({ textSize: Math.max(0.85, +(prefs.textSize - 0.1).toFixed(1)) });
  const resetTextSize = () => updPrefs({ textSize: 1 });

  const resetAll = () => setPrefs(DEFAULT_PREFS);

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-5 left-5 z-40 w-12 h-12 rounded-full bg-violet-600 hover:bg-violet-700 text-white shadow-xl shadow-violet-600/30 flex items-center justify-center transition-transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-violet-300"
        aria-label="Ouvrir les options d'accessibilité"
        aria-expanded={open}
        aria-controls="a11y-toolbar-panel"
        data-testid="a11y-toolbar-trigger"
        title="Accessibilité"
      >
        <Accessibility size={22} />
      </button>

      {/* Panel */}
      {open && (
        <div
          id="a11y-toolbar-panel"
          role="dialog"
          aria-label="Options d'accessibilité"
          className="fixed bottom-20 left-5 z-40 w-80 max-w-[calc(100vw-2.5rem)] rounded-2xl bg-white border border-slate-200 shadow-2xl p-5"
          data-testid="a11y-toolbar-panel"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Accessibility size={18} className="text-violet-600" />
              <h2 className="font-semibold text-slate-900 text-sm">Accessibilité</h2>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="w-7 h-7 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500"
              aria-label="Fermer"
              data-testid="a11y-toolbar-close"
            >
              <X size={16} />
            </button>
          </div>

          {/* Text size */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1.5">
                <Type size={13} /> Taille du texte
              </span>
              <span className="text-xs text-slate-500 tabular-nums">{Math.round(prefs.textSize * 100)}%</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={decTextSize}
                className="flex-1 py-2 rounded-lg border border-slate-200 hover:border-violet-300 text-sm font-semibold disabled:opacity-50"
                disabled={prefs.textSize <= 0.85}
                aria-label="Diminuer la taille du texte"
                data-testid="a11y-text-decrease"
              >
                A−
              </button>
              <button
                onClick={resetTextSize}
                className="px-3 py-2 rounded-lg border border-slate-200 hover:border-violet-300 text-sm"
                aria-label="Réinitialiser la taille du texte"
                data-testid="a11y-text-reset"
              >
                A
              </button>
              <button
                onClick={incTextSize}
                className="flex-1 py-2 rounded-lg border border-slate-200 hover:border-violet-300 text-base font-semibold disabled:opacity-50"
                disabled={prefs.textSize >= 1.4}
                aria-label="Augmenter la taille du texte"
                data-testid="a11y-text-increase"
              >
                A+
              </button>
            </div>
          </div>

          {/* Toggles */}
          <ToggleRow
            label="Contraste élevé"
            description="Renforce la lisibilité (texte plus sombre, bordures marquées)."
            icon={Contrast}
            value={prefs.highContrast}
            onChange={(v) => updPrefs({ highContrast: v })}
            testid="a11y-toggle-contrast"
          />
          <ToggleRow
            label="Mode sombre"
            description="Réduit la fatigue oculaire en faible luminosité."
            icon={prefs.darkMode ? Sun : Moon}
            value={prefs.darkMode}
            onChange={(v) => updPrefs({ darkMode: v })}
            testid="a11y-toggle-dark"
          />
          <ToggleRow
            label="Réduire les animations"
            description="Limite les transitions et les mouvements à l'écran."
            icon={ALargeSmall}
            value={prefs.reducedMotion}
            onChange={(v) => updPrefs({ reducedMotion: v })}
            testid="a11y-toggle-motion"
          />

          {/* Reset */}
          <button
            onClick={resetAll}
            className="mt-2 w-full inline-flex items-center justify-center gap-2 py-2 rounded-lg text-xs text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-colors"
            data-testid="a11y-reset-all"
          >
            <RefreshCw size={12} /> Réinitialiser
          </button>

          <p className="text-[10px] text-slate-400 mt-3 leading-relaxed">
            Tes préférences sont sauvegardées dans ce navigateur uniquement (localStorage). Aucune donnée envoyée au serveur.
          </p>
        </div>
      )}
    </>
  );
}

function ToggleRow({ label, description, icon: Icon, value, onChange, testid }) {
  return (
    <label className="flex items-start gap-3 py-2 cursor-pointer" data-testid={testid}>
      <div className="w-8 h-8 rounded-lg bg-violet-50 border border-violet-100 flex items-center justify-center shrink-0">
        <Icon size={14} className="text-violet-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 leading-snug">{label}</p>
        <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{description}</p>
      </div>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
        aria-label={label}
      />
      <span className="relative w-9 h-5 rounded-full bg-slate-200 peer-checked:bg-violet-500 transition-colors shrink-0 mt-0.5 cursor-pointer">
        <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4" />
      </span>
    </label>
  );
}
